import { StytchHeadlessClient } from '@stytch/vanilla-js/headless';
import { ulid } from 'ulid';
import { proxy } from 'valtio';
import { devtools } from 'valtio/utils';

import { setDatadogUser } from '@cartwhl/tracking/datadog';
import { getApiDomain } from '@cartwhl/common/api/api-utils';
import manageCartwheelSession from '@cartwhl/common/api/manageCartwheelSession';
import * as noticesStore from '@cartwhl/common/stores/noticesStore';
import * as userStore from '@cartwhl/common/stores/userStore';

import { getCookieSuffix } from '../utils';
import {
  ANONYMOUS_USER_ID,
  createAnomymousUserCookieIfNeeded,
  getStytchToken,
} from '../utils';
import handleGenericErrors from '../utils/handleGenericErrors';

// https://html.spec.whatwg.org/multipage/input.html#valid-e-mail-address
const emailRegex =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;

export const StytchMessages = {
  bad_domain_for_stytch_sdk: 'System error. Please notify support.',
  no_match_for_provided_magic_link_url: 'Something wen’t wrong',
  duplicate_email:
    'A user with the specified email already exists. Try logging in.',
  breached_password:
    'Password appears in a list of breached passwords. Please choose a different one.',
  weak_password: 'Password doesn’t meet our strength requirements.',
  reset_password: 'Please reset your password before logging in again.',
  no_user_password: 'Try signing in using Google login.',
};
export type StytchAPIErrorType = keyof typeof StytchMessages;

export type StytchAPIError = {
  error_type: StytchAPIErrorType;
  status_code: number;
  request_id: string;
  error_message: string;
  error_url: string;
};

export type AuthStoreState = {
  authStatus: 'anonymous' | 'user';
  userID?: string;
  /** Metrics working-session ID (ses-{ulid}); sliding 1-hour TTL in localStorage. */
  workingSessionID?: string;
};

const SESSION_DURATION_MINUTES = 60 * 24 * 7;
/** TTL for the metrics workingSessionID stored in localStorage. */
const WORKING_SESSION_TTL_MS = 60 * 60 * 1000;
const WORKING_SESSION_STORAGE_KEY = 'cartwheelWorkingSession';

type StoredWorkingSession = {
  workingSessionID: string;
  lastActivityAt: number;
};

let stytchClient = new StytchHeadlessClient(getStytchToken());

export const state = proxy<AuthStoreState>({
  authStatus: getInitialStatus(),
  userID: undefined,
  workingSessionID: undefined,
});

if (process.env.NODE_ENV === 'development') {
  devtools(state, { name: 'Auth', enabled: true });
}

//// Actions:

export async function init() {
  createAnomymousUserCookieIfNeeded();
  state.userID = getUserIDFromCookie();
  state.workingSessionID = ensureWorkingSessionID();

  // If the user is anonymous, we don't need to check the session
  if (state.userID === ANONYMOUS_USER_ID || window.self !== window.parent) {
    state.authStatus = 'anonymous';
    // check their localStorage for an anonymous user ID, create one and set it if it doesn't exist.
    // gets used so that we can deduplicate sessions for anonymous users in Datadog
    let anonymousUserID = localStorage.getItem('anonymousUserID');
    if (!anonymousUserID) {
      anonymousUserID = `anonymous-${crypto.randomUUID()}`;
      localStorage.setItem('anonymousUserID', anonymousUserID);
    }
    setDatadogUser({
      id: anonymousUserID,
      isAnonymous: true,
      email: '',
      name: '',
      planType: '',
    });
  } else {
    state.authStatus = 'user';
    await userStore.load(false);
  }
}

/**
 * Returns the current metrics working-session ID, creating/renewing one if needed.
 * Touches lastActivityAt on each call (sliding 1-hour window). Safe before/after init().
 */
export function getWorkingSessionID(): string {
  const workingSessionID = ensureWorkingSessionID();
  state.workingSessionID = workingSessionID;
  return workingSessionID;
}

/** Bumps lastActivityAt to now, extending the sliding working-session window. */
export function touchWorkingSessionActivity(): void {
  state.workingSessionID = ensureWorkingSessionID();
}

export async function logout() {
  userStore.clear();
  clearWorkingSession();
  const { data, error } = await manageCartwheelSession('logout');
  if (error) {
    await handleGenericErrors(error, { suppressNotices: true });
  } else if (data) {
    state.authStatus = 'anonymous';
    state.userID = undefined;
    window.location.href = '/';
    // reset the stytch client in case the user tries to immediately login in again
    stytchClient = new StytchHeadlessClient(getStytchToken());
  }
}

export async function loginWithPassword(email: string, password: string) {
  if (email === '' || !emailRegex.test(email)) {
    noticesStore.error('Please enter a valid email address');
    return;
  }
  if (!password) {
    noticesStore.error('Please enter a password');
    return;
  }
  try {
    const res = await stytchClient.passwords.authenticate({
      email,
      password,
      session_duration_minutes: SESSION_DURATION_MINUTES,
    });
    await handleTokenAfterSignin(res.session_token);
    await init();

    // Handle redirect after successful login
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirectTo');
    if (redirectTo) {
      window.location.href = buildRedirectUrl(redirectTo);
    } else {
      // Default redirect to home
      window.location.href = '/';
    }
  } catch (e) {
    const error = e as StytchAPIError;
    const type = error.error_type;
    const message = StytchMessages[type] || error.error_message;
    noticesStore.error(message);
  }
}

export async function signupWithPassword(
  firstname: string,
  lastname: string,
  email: string,
  password: string,
) {
  if (!firstname || !lastname) {
    noticesStore.error('Please enter first and last name');
    return;
  }
  if (!email || !emailRegex.test(email)) {
    noticesStore.error('Please enter a valid email address');
    return;
  }
  if (!password) {
    noticesStore.error('Please enter a password');
    return;
  }
  try {
    const res = await stytchClient.passwords.create({
      email,
      password,
      session_duration_minutes: SESSION_DURATION_MINUTES,
    });
    await stytchClient.user.update({
      name: {
        first_name: firstname,
        last_name: lastname,
      },
    });
    const isSignedIn = await handleTokenAfterSignin(res.session_token);
    if (isSignedIn) {
      // POST LAUNCH TODO: Figure out if we can change how this email looks so that it's more user friendly. JJ doesn't like the copy on it and the "Log in" button
      // await verifyEmail(email); // Send email verification
      return true;
    } else {
      // Occurs if users used a bad invite link
      await deleteUser(res.user_id);
    }
  } catch (error: unknown) {
    console.log(error);
    if (error && typeof error === 'object' && 'error_type' in error) {
      const type = error.error_type as StytchAPIErrorType;
      if (type === 'duplicate_email') {
        // Special case with more explanation needed
        noticesStore.error(
          'Email is already registered. Redirecting you to login...',
          5000,
        );
        let path = '/login';
        path = appendLoginQueryParams(path);
        console.log(`Navigating to ${path}`);
        setTimeout(() => (window.location.href = path), 2500);
      } else {
        const message = StytchMessages[type] || 'Something went wrong.';
        noticesStore.error(message);
      }
    }
  }
}

export async function requestPasswordReset(email: string) {
  const { error } = await manageCartwheelSession('resetPassword', {
    email: encodeURIComponent(email),
  });
  if (error) {
    await handleGenericErrors(error);
  } else {
    noticesStore.info('Check your email for a reset link!');
  }
}

export async function resetPassword(password: string, token: string) {
  try {
    await stytchClient.passwords.resetByEmail({
      token,
      password,
      session_duration_minutes: SESSION_DURATION_MINUTES,
    });
    noticesStore.info(
      'Password reset successfully! Please login with your new password.',
    );
  } catch (error: unknown) {
    console.log(error);
    if (error && typeof error === 'object' && 'error_type' in error) {
      const message =
        StytchMessages[error.error_type as StytchAPIErrorType] ||
        'Something went wrong.';
      noticesStore.error(message);
    }
  }
}

export async function verifyEmail(email: string) {
  const queryParams: Record<string, string> = {
    email: encodeURIComponent(email),
  };
  // if the user has a redirectTo param, we need to add it to the query params
  const params = new URLSearchParams(window.location.search);

  // INFO: Adding another encoding is intentional so that it is handled correctly passing
  // through Stytch's RedirectURL mechanism
  if (params.has('redirectTo')) {
    queryParams['redirectTo'] = encodeURIComponent(
      params.get('redirectTo') || '',
    );
  }
  const session = getSessionFromStytchCookie();
  if (session) {
    queryParams['token'] = session;
  }
  const { error } = await manageCartwheelSession('verifyEmail', queryParams);
  if (error) {
    await handleGenericErrors(error);
    return false;
  } else {
    return true;
  }
}

export async function continueWithGoogle() {
  stytchClient.oauth.google.start({
    login_redirect_url: getRedirectUrl(),
    signup_redirect_url: getRedirectUrl(),
    custom_scopes: ['openid', 'email', 'profile'],
  });
}

export async function continueWithFacebook() {
  stytchClient.oauth.facebook.start({
    login_redirect_url: getRedirectUrl(),
    signup_redirect_url: getRedirectUrl(),
    custom_scopes: [],
  });
}

//// Helper functions:

/**
 * Build the full redirect URL based on the redirectTo parameter.
 * Mirrors the backend logic in lambdaLoginEntry.ts buildRedirectBase().
 *
 * Examples:
 * - "studio" → https://studio.getcartwheel.com or https://studio-staging.getcartwheel.com
 * - "apps/comic" → https://app.getcartwheel.com/comic
 * - "/checkout" → /checkout (same origin)
 */
export function buildRedirectUrl(redirectTo: string): string {
  const hostname = window.location.hostname;

  // Determine environment from current hostname
  const isLocal = hostname.includes('local');
  const isStaging = hostname.includes('staging');

  // Get the disambiguator suffix for subdomains
  const disambiguator = isLocal ? '' : isStaging ? '-staging' : '';

  // If it's a relative path (starts with /), keep it on the same origin
  if (redirectTo.startsWith('/')) {
    return redirectTo;
  }

  // Build redirect base and path based on prefix
  let baseUrl: string;
  let path: string;

  if (redirectTo.startsWith('studio')) {
    if (isLocal) {
      baseUrl = 'http://local.getcartwheel.com:3001';
    } else {
      baseUrl = `https://studio${disambiguator}.getcartwheel.com`;
    }
    // Remove 'studio' prefix from path, e.g., 'studio/project' → '/project'
    path = redirectTo.replace(/^studio\/?/, '/');
  } else if (redirectTo.startsWith('labeler')) {
    if (isLocal) {
      baseUrl = 'http://local.getcartwheel.com:3010';
    } else {
      baseUrl = `https://labeler${disambiguator}.getcartwheel.com`;
    }
    path = redirectTo.replace(/^labeler\/?/, '/');
  } else if (redirectTo.startsWith('apps')) {
    if (isLocal) {
      baseUrl = 'http://local.getcartwheel.com:3010';
    } else {
      baseUrl = `https://app${disambiguator}.getcartwheel.com`;
    }
    // Remove 'apps' prefix from path, e.g., 'apps/comic' → '/comic'
    path = redirectTo.replace(/^apps\/?/, '/');
  } else {
    // Default: stay on the main site
    if (isLocal) {
      baseUrl = 'http://local.getcartwheel.com:3000';
    } else if (isStaging) {
      baseUrl = 'https://staging.getcartwheel.com';
    } else {
      baseUrl = 'https://getcartwheel.com';
    }
    path = redirectTo.startsWith('/') ? redirectTo : `/${redirectTo}`;
  }

  // Ensure path starts with /
  if (path && !path.startsWith('/')) {
    path = `/${path}`;
  }

  return `${baseUrl}${path}`;
}

async function deleteStytchCookies() {
  document.cookie = `stytch_session=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
  document.cookie = `stytch_session_jwt=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
}

async function deleteUser(userID: string) {
  deleteStytchCookies();
  const { error } = await manageCartwheelSession('deleteUser', {
    userID,
  });
  if (error) {
    await handleGenericErrors(error);
  }
}

async function handleTokenAfterSignin(token: string) {
  const params = new URLSearchParams(window.location.search);
  let queryParams = `?token=${token}`;
  if (params.has('invite')) {
    queryParams += `&invite=${params.get('invite')}`;
  }
  if (params.has('redirectTo')) {
    queryParams += `&redirectTo=${params.get('redirectTo')}`;
  }

  const { data, error } = await manageCartwheelSession(
    'passwordLogin',
    queryParams,
  );
  if (error) {
    await handleGenericErrors(error);
    return false;
  } else if (data) {
    return true;
  }
  return false;
}

function getRedirectUrl() {
  let redirectURL = getApiDomain('auth');
  redirectURL = appendLoginQueryParams(redirectURL);

  return redirectURL;
}

function appendLoginQueryParams(path: string) {
  let url = path.slice(0);
  const params = new URLSearchParams(window.location.search);

  // INFO: Adding another encoding is intentional so that it is handled correctly passing
  // through Stytch's RedirectURL mechanism
  if (params.has('redirectTo')) {
    url += `${url.includes('?') ? '&' : '?'
      }redirectTo=${encodeURIComponent(params.get('redirectTo') || '')}`;
  }
  if (params.has('invite')) {
    url += `${url.includes('?') ? '&' : '?'}invite=${params.get('invite')}`;
  }
  return url;
}

function getInitialStatus(): 'anonymous' | 'user' {
  const userID = getUserIDFromCookie();
  if (userID && userID !== ANONYMOUS_USER_ID) {
    return 'user';
  } else {
    return 'anonymous';
  }
}

function getUserIDFromCookie() {
  const suffix = getCookieSuffix();
  const cookies = document.cookie.split('; ');
  const cartwheelUserCookie = cookies.find((cookie) =>
    cookie.startsWith(`cartwheelUserID${suffix}=`),
  );
  if (cartwheelUserCookie) {
    const userID = cartwheelUserCookie.split('=')[1];
    return userID;
  }
}

function getSessionFromStytchCookie() {
  const cookies = document.cookie.split('; ');
  const stytchSessionCookie = cookies.find((cookie) =>
    cookie.startsWith('stytch_session'),
  );
  if (stytchSessionCookie) {
    const session = stytchSessionCookie.split('=')[1];
    return session;
  }
}

function generateWorkingSessionID() {
  return `ses-${ulid()}`;
}

function readStoredWorkingSession(): StoredWorkingSession | undefined {
  try {
    const raw = localStorage.getItem(WORKING_SESSION_STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<StoredWorkingSession>;
    if (
      typeof parsed.workingSessionID !== 'string' ||
      parsed.workingSessionID.length === 0 ||
      typeof parsed.lastActivityAt !== 'number'
    ) {
      return undefined;
    }
    return {
      workingSessionID: parsed.workingSessionID,
      lastActivityAt: parsed.lastActivityAt,
    };
  } catch {
    return undefined;
  }
}

function writeStoredWorkingSession(session: StoredWorkingSession) {
  localStorage.setItem(WORKING_SESSION_STORAGE_KEY, JSON.stringify(session));
}

function clearWorkingSession() {
  localStorage.removeItem(WORKING_SESSION_STORAGE_KEY);
  state.workingSessionID = undefined;
}

/**
 * Returns a valid metrics workingSessionID. Reuses the stored ID when still
 * within WORKING_SESSION_TTL_MS and bumps lastActivityAt; otherwise mints a
 * new ses-{ulid}.
 */
function ensureWorkingSessionID(): string {
  const now = Date.now();
  const existing = readStoredWorkingSession();
  if (
    existing &&
    now - existing.lastActivityAt < WORKING_SESSION_TTL_MS
  ) {
    writeStoredWorkingSession({
      workingSessionID: existing.workingSessionID,
      lastActivityAt: now,
    });
    return existing.workingSessionID;
  }

  const session: StoredWorkingSession = {
    workingSessionID: generateWorkingSessionID(),
    lastActivityAt: now,
  };
  writeStoredWorkingSession(session);
  return session.workingSessionID;
}
